// Sticky Announcement Bar and Header functionality
class StickyAnnouncementHeader extends HTMLElement {
  constructor() {
    super();
    this.header = document.querySelector('sticky-header');
    this.announcementBar = document.querySelector('.announcement-bar-section');
    this.headerHeight = this.header?.offsetHeight || 0;
    this.announcementHeight = this.announcementBar?.offsetHeight || 36;
    this.lastScrollPosition = window.scrollY;
    this.scrollThreshold = 50; // Smaller threshold to match header behavior
    this.scrollingDown = false;
    this.isHomepage = document.body.classList.contains('template-index');
    this.isScrolledPast = window.scrollY > this.announcementHeight;
    this.searchOpen = false;
    this.wasAnnouncementHiddenBeforeSearch = false;
    this.headerWasOpenBeforeSearch = false;

    this.onScrollHandler = this.onScroll.bind(this);

    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    window.addEventListener('resize', () => {
      this.headerHeight = this.header?.offsetHeight || 0;
      this.announcementHeight = this.announcementBar?.offsetHeight || 36;
    });

    // Set initial header position based on page type
    if (this.isHomepage) {
      this.header.setAttribute('data-toggle', 'open');
    } else {
      this.header.setAttribute('data-toggle', 'close');
    }

    // Initialize state
    if (window.scrollY > this.announcementHeight) {
      document.documentElement.classList.add('scrolled-past-header');
    }

    this.setupSearchWatcher();

    // Add event listeners for megamenu toggle changes
    this.setupMegaMenuObserver();

    // Add event listener for mobile menu drawer toggle changes
    this.setupMobileMenuObserver();

    // Check for any open mega menus on page load
    document.addEventListener('DOMContentLoaded', () => {
      this.checkForOpenMegaMenus();
    });
  }

  onScroll() {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      const rawScrollTop = window.scrollY;
      let scrollTop = rawScrollTop;
      const scrollDelta = scrollTop - this.lastScrollPosition;

      // Check if any mega menu is open OR mobile menu drawer is open
      const anyMegaMenuOpen = Array.from(document.querySelectorAll('.mega-menu__content')).some(
        (menu) => menu.getAttribute('data-toggle') === 'open'
      );
      const mobileMenuOpen = document.querySelector('.menu-drawer-container[open]') !== null;
      const anyMenuOpen = anyMegaMenuOpen || mobileMenuOpen;

      // Get sticky header gradient element
      const stickyHeaderGradient = document.querySelector('sticky-header.gradient');

      // Handle scrolled state for background color change
      const scrollPastThreshold = this.announcementHeight + 20;
      const scrollResetThreshold = this.announcementHeight - 20;

      if (!this.isScrolledPast && scrollTop > scrollPastThreshold) {
        document.documentElement.classList.add('scrolled-past-header');
        // Add scrolled class to sticky-header.gradient when scrolling past threshold
        if (stickyHeaderGradient) {
          stickyHeaderGradient.classList.add('scrolled');
        }
        this.isScrolledPast = true;
      } else if (this.isScrolledPast && scrollTop < scrollResetThreshold) {
        document.documentElement.classList.remove('scrolled-past-header');
        document.documentElement.classList.remove('hide-announcement');

        // Only remove scrolled class if no menu is open
        if (!anyMenuOpen && stickyHeaderGradient) {
          stickyHeaderGradient.classList.remove('scrolled');
        }

        this.isScrolledPast = false;
      }

      // Handle hiding/showing on scroll direction - match header behavior
      if (Math.abs(scrollDelta) >= 9) {
        // Small threshold to detect direction changes
        this.scrollingDown = scrollDelta > 0;

        if (this.scrollingDown && scrollTop > this.scrollThreshold) {
          // Scrolling down - hide announcement bar
          if (!this.searchOpen) {
            document.documentElement.classList.add('hide-announcement');
          }
          this.header.setAttribute('data-toggle', 'close');

          // If any menu is open, ensure scrolled class is added
          if (anyMenuOpen && stickyHeaderGradient) {
            stickyHeaderGradient.classList.add('scrolled');
          }
        } else if (!this.scrollingDown) {
          // Scrolling up - show announcement bar
          document.documentElement.classList.remove('hide-announcement');
          // When scrolling up and past the threshold, position header below announcement bar
          if (!this.searchOpen) {
            if (scrollTop > this.announcementHeight && scrollTop > 80) {
              this.header.setAttribute('data-toggle', 'open'); // header 33px
              if (stickyHeaderGradient) {
                stickyHeaderGradient.classList.add('scrolled');
              }
            } else if (!this.isHomepage) {
              this.header.setAttribute('data-toggle', 'close'); // non-homepage reset
              // If any menu is open, ensure scrolled class is added
              if (anyMenuOpen && stickyHeaderGradient) {
                stickyHeaderGradient.classList.add('scrolled');
              }
            } else if (scrollTop < 80 && !this.scrollingDown) {
              this.header.setAttribute('data-toggle', 'close'); // when going slow to very top

              // Only remove scrolled class if no menu is open AND not scrolled past header
              if (!anyMenuOpen && stickyHeaderGradient) {
                const isScrolledPastHeader = document.documentElement.classList.contains('scrolled-past-header');
                if (!isScrolledPastHeader) {
                  stickyHeaderGradient.classList.remove('scrolled');
                }
              }
            } else {
              this.header.setAttribute('data-toggle', 'open'); // homepage
              if (stickyHeaderGradient) {
                stickyHeaderGradient.classList.add('scrolled');
              }
            }
          }
        }
      }

      this.lastScrollPosition = scrollTop;
    }, this.debounceDelay);
  }

  // Monitor megamenu toggle state changes
  setupMegaMenuObserver() {
    // Wait for DOM to be fully loaded
    document.addEventListener('DOMContentLoaded', () => {
      // Set up observers for all megamenu content elements
      const megaMenuContents = document.querySelectorAll('.mega-menu__content');

      megaMenuContents.forEach((menuContent) => {
        // Watch for attribute changes, specifically the data-toggle attribute
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.attributeName === 'data-toggle') {
              const isOpen = menuContent.getAttribute('data-toggle') === 'open';
              const stickyHeaderGradient = document.querySelector('sticky-header.gradient');

              if (isOpen) {
                // Only add scrolled class if search is not already open
                // (search already sets data-toggle="open" which positions the header)
                if (!this.searchOpen) {
                  this.header.classList.add('scrolled');
                  if (stickyHeaderGradient) {
                    stickyHeaderGradient.classList.add('scrolled');
                  }
                }
              } else {
                // Check if any other megamenus are open before removing the class
                const anyMenuOpen = Array.from(document.querySelectorAll('.mega-menu__content')).some(
                  (menu) => menu !== menuContent && menu.getAttribute('data-toggle') === 'open'
                );

                if (!anyMenuOpen) {
                  // 2. Only remove the scrolled class if no menu is open AND HTML doesn't have scrolled-past-header AND search is not open
                  const isScrolledPastHeader = document.documentElement.classList.contains('scrolled-past-header');

                  if (!isScrolledPastHeader && !this.searchOpen) {
                    this.header.classList.remove('scrolled');
                    if (stickyHeaderGradient) {
                      stickyHeaderGradient.classList.remove('scrolled');
                    }
                  }
                }
              }
            }
          });
        });

        // Start observing the element for attribute changes
        observer.observe(menuContent, { attributes: true });
      });
    });
  }

  // Monitor mobile menu drawer toggle state changes
  setupMobileMenuObserver() {
    document.addEventListener('DOMContentLoaded', () => {
      const menuDrawerContainer = document.querySelector('.menu-drawer-container');
      if (!menuDrawerContainer) return;

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
            const isOpen = menuDrawerContainer.hasAttribute('open');
            const stickyHeaderGradient = document.querySelector('sticky-header.gradient');

            if (isOpen) {
              // Add scrolled class when mobile menu opens
              this.header.classList.add('scrolled');
              if (stickyHeaderGradient) {
                stickyHeaderGradient.classList.add('scrolled');
              }
            } else {
              // Only remove scrolled class if no mega menu is open AND not scrolled past header
              const anyMegaMenuOpen = Array.from(document.querySelectorAll('.mega-menu__content')).some(
                (menu) => menu.getAttribute('data-toggle') === 'open'
              );

              if (!anyMegaMenuOpen) {
                const isScrolledPastHeader = document.documentElement.classList.contains('scrolled-past-header');

                if (!isScrolledPastHeader) {
                  this.header.classList.remove('scrolled');
                  if (stickyHeaderGradient) {
                    stickyHeaderGradient.classList.remove('scrolled');
                  }
                }
              }
            }
          }
        });
      });

      // Start observing the menu drawer container for attribute changes
      observer.observe(menuDrawerContainer, { attributes: true });
    });
  }

  // Check for any open mega menus or mobile menu and apply scrolled class if needed
  checkForOpenMegaMenus() {
    const anyMegaMenuOpen = Array.from(document.querySelectorAll('.mega-menu__content')).some(
      (menu) => menu.getAttribute('data-toggle') === 'open'
    );
    const mobileMenuOpen = document.querySelector('.menu-drawer-container[open]') !== null;
    const anyMenuOpen = anyMegaMenuOpen || mobileMenuOpen;

    if (anyMenuOpen) {
      // Add scrolled class to both header and sticky-header.gradient
      this.header.classList.add('scrolled');
      const stickyHeaderGradient = document.querySelector('sticky-header.gradient');
      if (stickyHeaderGradient) {
        stickyHeaderGradient.classList.add('scrolled');
      }
    }
  }

  setupSearchWatcher() {
    const updateSearchState = () => {
      const anyOpen = Boolean(document.querySelector('.header__search details[open]'));

      if (anyOpen === this.searchOpen) return;

      this.searchOpen = anyOpen;

      const headerWasOpen = this.header?.getAttribute('data-toggle') === 'open';

      if (this.searchOpen) {
        this.wasAnnouncementHiddenBeforeSearch = document.documentElement.classList.contains('hide-announcement');
        this.headerWasOpenBeforeSearch = headerWasOpen;
        document.documentElement.classList.remove('hide-announcement');

        if (this.header) {
          this.header.setAttribute('data-toggle', 'open');
        }
      } else {
        if (this.wasAnnouncementHiddenBeforeSearch) {
          document.documentElement.classList.add('hide-announcement');
        } else {
          document.documentElement.classList.remove('hide-announcement');
        }

        this.wasAnnouncementHiddenBeforeSearch = document.documentElement.classList.contains('hide-announcement');

        if (
          !this.headerWasOpenBeforeSearch &&
          this.header &&
          !document.documentElement.classList.contains('mega-menu-open')
        ) {
          this.header.setAttribute('data-toggle', 'close');
        }

        // Re-run scroll eval to align header state once search closes
        this.onScroll();
      }
    };

    document.addEventListener('DOMContentLoaded', () => {
      this.wasAnnouncementHiddenBeforeSearch = document.documentElement.classList.contains('hide-announcement');
      updateSearchState();

      document.querySelectorAll('.header__search details').forEach((detailsEl) => {
        detailsEl.addEventListener('toggle', updateSearchState);
      });
    });
  }
}

customElements.define('sticky-announcement-header', StickyAnnouncementHeader);
